import { AddPaymentModal } from "@entities/accounting/ui/AddPaymentModal";
import { Button, Flex, Input, Space, Tag, Typography } from "antd";
import { ColumnsType } from "antd/es/table";
import { Building2, Download, Plus, Search, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import styles from "./PaymentsTab.module.scss";

import { useGetPayments } from "@entities/accounting/lib/hooks/useGetPayments";
import { IPayment } from "@entities/accounting/lib/types";
import { useGetBrands } from "@entities/brand";
import { Table } from "@shared/ui";
import { SelectButton } from "@shared/ui/Select";

export function PaymentsTab() {
  const [searchQuery, setSearchQuery] = useState("");
  const [brandFilter, setBrandFilter] = useState<string[]>([]);
  const [typeFilter, setTypeFilter] = useState<string[]>([]);
  const [brandSearch, setBrandSearch] = useState("");
  const [isAddPaymentModalOpen, setIsAddPaymentModalOpen] = useState(false);

  const { payments, isFetching, isFetchingNextPage } = useGetPayments({
    searchQuery: searchQuery,
    brandIds: brandFilter,
    subTypes: typeFilter,
  });
  const { brands, isFetching: areBrandsLoading } = useGetBrands(
    10,
    1,
    brandSearch
  );
  const brandOptions = useMemo(
    () => brands?.data?.map((b) => ({ label: b.name, value: b._id })) ?? [],
    [brands]
  );

  const columns: ColumnsType<IPayment> = [
    {
      title: "Date",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) =>
        new Date(date).toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }),
    },
    {
      title: "Payment ID",
      dataIndex: "_id",
      key: "_id",
      render: (id, record) => (
        <Flex vertical>
          <b>{id}</b>
          <Typography.Text
            type={record.subType === "Refund" ? "danger" : "success"}
            style={{
              color:
                record.subType === "Received"
                  ? "#16a34a"
                  : record.subType === "Tds"
                    ? "#0f766e"
                    : "#dc2626",
            }}
          >
            {record.subType}
          </Typography.Text>
        </Flex>
      ),
    },
    {
      title: "Brand",
      dataIndex: "brandId",
      key: "brandId",
      render: (brandId) => (
        <Flex align="center" gap={5}>
          <Building2 size={12} />
          {brands?.data?.find((b) => b._id === brandId)?.name || brandId}
        </Flex>
      ),
    },
    {
      title: "Invoices",
      dataIndex: "brandAccountingIds",
      key: "invoices",
      render: (brandAccountingIds: IPayment["brandAccountingIds"]) =>
        brandAccountingIds ? (
          <Space wrap>
            {brandAccountingIds?.map((info) => (
              <Tag key={info._id}>{info.accountingNumber}</Tag>
            ))}
          </Space>
        ) : null,
    },
    {
      title: "Method / Ref",
      key: "method",
      render: (_, record) => (
        <div>
          <div>{record.method}</div>
          <div style={{ fontSize: 11, color: "#888" }}>
            {record.tnxReference}
          </div>
        </div>
      ),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      align: "right",
      render: (amount, record) => {
        const isCredit = record.type === "Credit";
        return (
          <b
            style={{
              color:
                record.subType === "Refund"
                  ? "#dc2626"
                  : record.subType === "Tds"
                    ? "#0f766e"
                    : "#16a34a",
            }}
          >
            {isCredit ? "-" : ""}₹{amount}
          </b>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      align: "right",
      render: () => (
        <Button type="text" danger size="small" icon={<Trash2 size={14} />} />
      ),
    },
  ];

  return (
    <div className={styles.paymentsTabContainer}>
      {/* <Row gutter={[24, 24]} className={styles.statsRow}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Total Received"
              value={0}
              // prefix="₹"
              valueStyle={{ color: "#16a34a" }}
              prefix={<ArrowDownLeft size={20} />}
            />
          </Card>
        </Col>
      </Row> */}

      <Table
        columns={columns}
        dataSource={payments}
        title={() => <div className={styles.controls}>
          {/* Search Row */}
          <div className={styles.searchRow}>
            <Input
              prefix={<Search size={16} />}
              placeholder="Search by description, reference, or invoice..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{width: 300}}
            />
            <div className={styles.actions}>
              <Button
                icon={<Plus size={16} />}
                type="primary"
                onClick={() => setIsAddPaymentModalOpen(true)}
              >
                Add Payment
              </Button>
              <Button icon={<Download size={16} />}>Export CSV</Button>
            </div>
          </div>
          <Flex gap="small" wrap="wrap" className={styles.filters}>
            <SelectButton
              label="All Brands"
              options={brandOptions}
              value={brandFilter}
              onChange={(value) => {
                setBrandFilter(value as string[]);
              }}
              mode="multiple"
              isLoading={areBrandsLoading}
              className={styles.select}
              searchable
              withFooterActions
              searchValue={brandSearch}
              onSearchChange={setBrandSearch}
            />
            <SelectButton
              label="All Types"
              options={[
                { value: "Received", label: "Received" },
                { value: "Refund", label: "Refund" },
                { value: "Tds", label: "TDS" },
              ]}
              value={typeFilter}
              onChange={(value) => {
                setTypeFilter(value as string[]);
              }}
              mode="multiple"
              className={styles.select}
              withFooterActions
            />
          </Flex>
        </div>}
        rowKey="_id"
        loading={isFetching && !isFetchingNextPage}
        // className={styles.table}
        pagination={{
          hideOnSinglePage: true,
        }}
        scroll={{
          x: "auto",
        }}
      />
      {isAddPaymentModalOpen ? (
        <AddPaymentModal
          isOpen={isAddPaymentModalOpen}
          onClose={() => setIsAddPaymentModalOpen(false)}
        />
      ) : null}
    </div>
  );
}
